import { ApolloError, useQuery } from "@apollo/client";
import React from "react";
import { IParticipantDetailsProps } from ".";
import { ErrorContext } from "../../../../context";
import { DynamicTextContextEnum } from "../../../../enums/DynamicTextContext";
import { ParticipantIdentityStatusEnum } from "../../../../enums/ParticipantIdentityStatusEnum";
import { strings } from "../../../../localisation";
import { ExecuteModel } from "../../../Steps/ExecuteModel";
import { IParticipantEmailDataModel } from "../../models/ParticipantEmail";
import { SendEmailExecuteModel } from "../../models/SendEmail/SendEmailExecuteModel";
import { getParticipants } from "./graphql/getParticipants";
import { ParticipantDetailsDiv } from "./ParticipantDetails.styles";
import { IParticipant, IParticipantResponse, ParticipantSelector, ParticipantSelectType } from "./ParticipantSelector";

export const ParticipantDetails = ({ IDs, DynamicTextContext, FormikProps, Shown }: IParticipantDetailsProps) => {
    // Context
    const errorContext = React.useContext(ErrorContext);

    const setAllParticipants = (ids: string[]) => {
        FormikProps.setFieldValue(
            nameof.full<ExecuteModel<IParticipantEmailDataModel>>((m) => m.formDataModel.ParticipantEntryIDs), ids ?? []);
    };

    const [participantIdIndex, setParticipantIdIndex] = React.useState(0);

    // Load the participants
    const { data } = useQuery<IParticipantResponse>(getParticipants, {
        variables: {
            id: IDs[participantIdIndex],
            participantType: DynamicTextContext,
            participantIdentityStatus: ParticipantIdentityStatusEnum.Known,
        },
        skip: participantIdIndex === IDs.length,
        fetchPolicy: "cache-first",
        onError: (error: ApolloError) => {
            if (participantIdIndex === IDs.length) {
                errorContext.SetError(error);
            } else {
                setParticipantIdIndex(participantIdIndex + 1);
            }
        },
        onCompleted: (response: IParticipantResponse) => {
            // If we have got more than one Concern/Incident/Contribution, set the ParticipantEntryIDs so we can preview
            if (IDs.length > 1) {
                if (response.participantsByType.length) {
                    const ids = response.participantsByType.map((participant: IParticipant) => participant.participantID);
                    setAllParticipants(ids);
                } else {
                    setParticipantIdIndex(participantIdIndex + 1);
                }
            }
        },
    });

    React.useEffect(() => {
        if (data && FormikProps.values.formDataModel.ParticipantSelectorOptions === ParticipantSelectType.All) {
            const ids = data.participantsByType.map((participant: IParticipant) => participant.participantID);
            setAllParticipants(ids);
        }
    }, [FormikProps.values.formDataModel.ParticipantSelectorOptions]);

    const selectionOptions = React.useMemo(() => {
        const options: { [type in ParticipantSelectType]?: string } = {};

        switch (DynamicTextContext) {
            case DynamicTextContextEnum.ConcernEntry:
                options[ParticipantSelectType.All] = strings.Label_ParticipantSelector_SendToAllInvolved;

                if (IDs.length === 1) {
                    options[ParticipantSelectType.SelectedParticipants] = strings.Label_ParticipantSelector_SendToSpecificParticipants;
                }

                break;
            case DynamicTextContextEnum.IncidentEntry:
                options[ParticipantSelectType.All] = strings.Label_ParticipantSelector_SendToAllInvolved;
                options[ParticipantSelectType.AllParticipants] = strings.Label_ParticipantSelector_SendToAllParticipants;

                if (IDs.length === 1) {
                    options[ParticipantSelectType.SelectedParticipants] = strings.Label_ParticipantSelector_SendToSpecificParticipants;
                }

                break;

            case DynamicTextContextEnum.ContributionEntry:
                options[ParticipantSelectType.All] = strings.Label_ParticipantSelector_SendToAllInvolved;

                if (IDs.length === 1) {
                    options[ParticipantSelectType.SelectedParticipants] = strings.Label_ParticipantSelector_SendToSpecificParticipants;
                }

                break;
        }

        return options;
    }, []);

    React.useEffect(() => {
        if (Shown) {
            const optionKeys = Object.keys(selectionOptions);
            if (optionKeys.length === 1) {
                FormikProps.setFieldValue(nameof.full<SendEmailExecuteModel<IParticipantEmailDataModel>>((m) => m.formDataModel.ParticipantSelectorOptions), optionKeys[0]);
            }
        }
    }, [Shown]);

    return (
        <ParticipantDetailsDiv>
            <ParticipantSelector
                FormikProps={FormikProps}
                Required={true}
                FieldLabel={strings.Label_ParticipantSelector_FieldLabel}
                SelectionTypeOptions={selectionOptions}
                Participants={data?.participantsByType || []} />
        </ParticipantDetailsDiv>
    );
};
